import { Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import * as R from "@/pages/reviewContent";

const IMP = { Critical: "bg-red-100 text-red-800", High: "bg-[#F6E1D8] text-[#9A4A33]", Medium: "bg-[#F4EBD9] text-[#8A6326]", Low: "bg-stone-100 text-stone-600" };
const Tag = ({ v }) => <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${IMP[v]}`}>{v}</span>;
const H2 = ({ n, children, id }) => (
  <h2 id={id} className="font-display text-2xl sm:text-3xl tracking-tight text-stone-900 mt-16 mb-5 flex items-baseline gap-3 scroll-mt-24">
    <span className="text-sm font-sans font-semibold text-[#C86D51]">{String(n).padStart(2, "0")}</span>{children}
  </h2>
);
const List = ({ title, items, color }) => (
  <div className="card p-5">
    <div className="font-semibold mb-3" style={{ color }}>{title}</div>
    <ul className="space-y-2 text-sm text-stone-700">{items.map((i) => <li key={i} className="flex gap-2"><span style={{ color }}>•</span>{i}</li>)}</ul>
  </div>
);

function Section({ s, n }) {
  return (
    <section data-testid={`review-section-${s.id}`}>
      <H2 n={n} id={s.id}>{s.title}</H2>
      <p className="text-stone-600 leading-relaxed mb-6">{s.intro}</p>
      <div className="space-y-3">
        {s.items.map((it) => (
          <div key={it.t} className="card p-5">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="font-semibold text-stone-900 mr-auto">{it.t}</h3>
              <Tag v={it.i} />
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#EEF3EF] text-[#4F6B55]">Effort {it.e}</span>
            </div>
            <p className="text-sm text-stone-600 leading-relaxed">{it.d}</p>
            <p className="text-xs text-stone-500 mt-2"><span className="font-semibold text-stone-700">User benefit:</span> {it.b}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const ReviewHeader = ({ user }) => (
  <header className="no-print sticky top-0 z-30 bg-[#FBF9F5]/90 backdrop-blur-md border-b border-stone-200/60">
    <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
      <Link to={user ? "/me" : "/"} data-testid="review-back-link" className="flex items-center gap-2 text-sm text-stone-600"><ArrowLeft className="h-4 w-4" /><Logo className="!text-base" /></Link>
      <button onClick={() => window.print()} data-testid="review-print-btn" className="flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-stone-300 hover:border-stone-900 chip"><Printer className="h-4 w-4" />Print / PDF</button>
    </div>
  </header>
);

const ReviewNav = () => (
  <nav className="no-print mt-8 flex flex-wrap gap-2 text-xs">
    {[["understanding", "Understanding"], ["verdict", "Verdict"], ["priorities", "Top priorities"], ...R.SECTIONS.map((s) => [s.id, s.title]), ["answers", "Keep / remove / redesign"], ["opportunities", "Opportunities"], ["connectors", "Connectors"], ["vision", "Full creative control"], ["roadmap", "Roadmap"]].map(([id, l]) => (
      <a key={id} href={`#${id}`} className="px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:border-stone-400 chip">{l}</a>
    ))}
  </nav>
);

const PriorityTable = ({ top }) => (
  <div className="card overflow-hidden" data-testid="review-priority-table">
    <table className="w-full text-sm">
      <thead className="bg-[#F5F2EC] text-left text-xs text-stone-500 uppercase tracking-wider">
        <tr><th className="p-3 w-8">#</th><th className="p-3">Recommendation</th><th className="p-3 hidden sm:table-cell">Area</th><th className="p-3">Importance</th><th className="p-3 hidden sm:table-cell">Effort</th></tr>
      </thead>
      <tbody>
        {top.map((t, i) => (
          <tr key={t.t} className="border-t border-stone-100 align-top">
            <td className="p-3 text-stone-400">{i + 1}</td>
            <td className="p-3"><div className="font-medium text-stone-900">{t.t}</div><div className="text-xs text-stone-500 mt-0.5">{t.b}</div></td>
            <td className="p-3 text-xs text-stone-500 hidden sm:table-cell">{t.area}</td>
            <td className="p-3"><Tag v={t.i} /></td>
            <td className="p-3 text-stone-600 hidden sm:table-cell">{t.e}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Connectors = () => (
  <div className="space-y-3" data-testid="review-connectors">
    {R.CONNECTORS.map((c) => (
      <div key={c.n} className="card p-5">
        <div className="flex flex-wrap items-center gap-2 mb-1.5"><h3 className="font-semibold mr-auto">{c.n}</h3><Tag v={c.i} /><span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{c.p}</span></div>
        <p className="text-sm text-stone-600 leading-relaxed">{c.why}</p>
      </div>
    ))}
  </div>
);

const Roadmap = () => (
  <div className="grid sm:grid-cols-3 gap-3">
    {R.ROADMAP.map((r) => (
      <div key={r.p} className="card p-5"><div className="font-semibold mb-3 text-sm">{r.p}</div>
        <ul className="space-y-1.5 text-xs text-stone-600">{r.items.map((i) => <li key={i}>· {i}</li>)}</ul></div>
    ))}
  </div>
);

export default function Review() {
  const { user } = useAuth();
  const top = R.SECTIONS.flatMap((s) => s.items.map((i) => ({ ...i, area: s.title }))).filter((i) => i.i === "Critical" || i.i === "High");
  const order = { Critical: 0, High: 1 };
  top.sort((a, b) => order[a.i] - order[b.i]);

  return (
    <div className="grain min-h-screen">
      <ReviewHeader user={user} />
      <article className="relative z-10 max-w-3xl mx-auto px-6 py-12" data-testid="review-report">
        <div className="eyebrow mb-3">{R.META.date} · Independent assessment</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">{R.META.title}</h1>
        <p className="text-stone-600 mt-4">{R.META.scope}</p>
        <div className="mt-6 rounded-2xl border border-[#E6CFC4] bg-[#FBF1EC] p-5 text-sm text-stone-700 leading-relaxed" data-testid="review-basis-note">
          <span className="font-semibold text-[#9A4A33]">Basis of this review. </span>{R.META.basis}
        </div>

        <ReviewNav />

        <H2 n={1} id="understanding">Understanding of CAMRYN</H2>
        <dl className="grid sm:grid-cols-2 gap-3">
          {R.UNDERSTANDING.map(([k, v]) => <div key={k} className="card p-5"><dt className="eyebrow mb-2">{k}</dt><dd className="text-sm text-stone-700 leading-relaxed">{v}</dd></div>)}
        </dl>

        <H2 n={2} id="verdict">The honest verdict</H2>
        <div className="space-y-4 text-stone-700 leading-relaxed">{R.VERDICT.map((p) => <p key={p}>{p}</p>)}</div>

        <H2 n={3} id="priorities">Top priorities, ranked</H2>
        <PriorityTable top={top} />
        <p className="text-xs text-stone-500 mt-2">Effort: S = days, M = 1 to 2 weeks, L = a month or more.</p>

        {R.SECTIONS.map((s, i) => <Section key={s.id} s={s} n={i + 4} />)}

        <H2 n={R.SECTIONS.length + 4} id="answers">What I'd keep, remove, redesign, build differently</H2>
        <div className="grid sm:grid-cols-2 gap-3">
          <List title="Keep" items={R.KEEP} color="#4F6B55" />
          <List title="Remove" items={R.REMOVE} color="#B4412C" />
          <List title="Redesign" items={R.REDESIGN} color="#8A6326" />
          <List title="Build differently" items={R.BUILD_DIFFERENTLY} color="#5A3E4E" />
        </div>

        <H2 n={R.SECTIONS.length + 5} id="opportunities">Opportunities you may have overlooked</H2>
        <div className="grid sm:grid-cols-2 gap-3">
          {R.OPPORTUNITIES.map(([k, v]) => <div key={k} className="card p-5"><div className="font-semibold mb-1.5">{k}</div><p className="text-sm text-stone-600 leading-relaxed">{v}</p></div>)}
        </div>

        <H2 n={R.SECTIONS.length + 6} id="connectors">Connectors I'd add</H2>
        <Connectors />

        <H2 n={R.SECTIONS.length + 7} id="vision">With complete creative control</H2>
        <div className="card p-6 bg-[#2D2926] border-[#2D2926] text-stone-200 space-y-3 leading-relaxed">
          {R.VISION.map((v, i) => <p key={v} className={i === 0 ? "font-display text-xl text-white" : "text-sm"}>{v}</p>)}
        </div>

        <H2 n={R.SECTIONS.length + 8} id="roadmap">Roadmap</H2>
        <Roadmap />

        {!user && (
          <div className="no-print mt-16 text-center">
            <Link to="/register" data-testid="review-try-rebuild-btn" className="btn-ink inline-flex px-7 py-3.5 rounded-full font-semibold">Try the rebuilt CAMRYN</Link>
          </div>
        )}
      </article>
    </div>
  );
}
